<script setup lang="ts">
defineOptions({
  name: 'TransferMethodModal',
})

import Switch from '@payjib/ui/components/Switch.vue'
import { useField, useForm } from 'vee-validate'
import { computed, watch } from 'vue'
import * as yup from 'yup'
import {
  createTransferMethodAction,
  updateTransferMethodAction,
} from '@/api/functions/settings/transfer-method-actions'
import type { TransferMethodItem } from '@/api/typings/settings.typing'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps<{
  modelValue: boolean
  method?: TransferMethodItem | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: []
  cancel: []
}>()

const toast = useToast()

const isEditMode = computed(() => props.method != null)

const actionLabel = computed(() => (isEditMode.value ? 'ویرایش روش پرداخت' : 'افزودن روش پرداخت جدید'))
const schema = yup.object({
  title: yup.string().required('عنوان انگلیسی الزامی است'),
  label: yup.string().required('عنوان فارسی الزامی است'),
  isActive: yup.boolean().required(),
})
const { handleSubmit, errors, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    title: '',
    label: '',
    isActive: true,
  },
})
const { value: title } = useField('title')
const { value: label } = useField('label')
const { value: isActive } = useField<boolean>('isActive')
watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return
    resetForm({
      values: {
        title: props.method?.title ?? '',
        label: props.method?.label ?? '',
        isActive: props.method ? props.method.status === 'ACTIVE' : true,
      },
    })
  },
  { immediate: true }
)
const createTransferMethodMutation = useMutationApi({
  mutationFn: (payload: { title: string; label: string; status: string }) => createTransferMethodAction(payload),
  onSuccess: () => {
    toast.success('روش پرداخت جدید با موفقیت ایجاد شد', 'موفقیت')
    emit('confirm')
    closeModal()
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const updateTransferMethodMutation = useMutationApi({
  mutationFn: (payload: { title: string; label: string; status: string }) =>
    updateTransferMethodAction(props.method!.id, payload),
  onSuccess: () => {
    toast.success('روش پرداخت با موفقیت ویرایش شد', 'موفقیت')
    emit('confirm')
    closeModal()
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const isSubmitting = computed(
  () => createTransferMethodMutation.isPending.value || updateTransferMethodMutation.isPending.value
)

const onSubmit = handleSubmit((values) => {
  const status = values.isActive ? 'ACTIVE' : 'INACTIVE'

  if (isEditMode.value) {
    return updateTransferMethodMutation.mutate({ title: values.title, label: values.label, status })
  }
  return createTransferMethodMutation.mutate({ title: values.title, label: values.label, status })
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
  <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
    background="#FFF" :closable="true" :title="actionLabel" closeDirection="left" class="update-request">

    <form @submit.prevent="onSubmit" class="flex flex-col min-h-[430px]">
      <div class="flex-1 overflow-auto">
        <div class="grid px-4 pt-6 pb-9 gap-6">
          <Input v-model="title" name="title" label="عنوان انگلیسی" type="text" :error="errors.title" />
          <Input v-model="label" name="label" label="عنوان فارسی" type="text" :error="errors.label" />

          <div class="flex items-center justify-between">
            <span class="text-[#1b325a] text-[14px] font-medium">وضعیت فعال بودن</span>
            <div class="flex items-center gap-2">
              <span class="text-[12px]" :class="isActive ? 'text-[#2d5396]' : 'text-[#8a8a8a]'">
                {{ isActive ? 'فعال' : 'غیرفعال' }}
              </span>
              <Switch v-model="isActive" />
            </div>
          </div>
        </div>
      </div>

      <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
        <Button type="submit" :label="actionLabel" :isAdmin="true" class="w-9/12 h-12 rounded-xl"
          :disabled="isSubmitting" :loading="isSubmitting" />
        <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
          class="w-4/12 h-12 rounded-xl border-2" :disabled="isSubmitting" />
      </div>
    </form>
  </Modal>
</template>
